import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaGlobe, FaPhone, FaEnvelope } from 'react-icons/fa';

interface NavbarProps {
  language: 'ar' | 'en';
  onLanguageChange: (lang: 'ar' | 'en') => void;
}

const Navbar: React.FC<NavbarProps> = ({ language, onLanguageChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: { ar: 'الرئيسية', en: 'Home' } },
    { path: '/about', label: { ar: 'من نحن', en: 'About' } },
    { path: '/services', label: { ar: 'خدماتنا', en: 'Services' } },
    { path: '/projects', label: { ar: 'مشاريعنا', en: 'Projects' } },
    { path: '/contact', label: { ar: 'اتصل بنا', en: 'Contact' } },
  ];

  const isRTL = language === 'ar';

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-black/20 backdrop-blur-sm'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20 px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 rtl:space-x-reverse">
            <img src="/images/logo.png" alt="Company Logo" className="w-72 h-72 object-contain drop-shadow-lg" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8 rtl:space-x-reverse">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-lg font-bold transition-colors duration-200 ${
                  location.pathname === item.path
                    ? 'text-primary-600'
                    : isScrolled ? 'text-gray-700 hover:text-primary-600' : 'text-white hover:text-primary-300'
                }`}
              >
                {item.label[language]}
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4 rtl:space-x-reverse">
            {/* Language Switcher */}
            <button
              onClick={() => onLanguageChange(language === 'ar' ? 'en' : 'ar')}
              className={`flex items-center space-x-2 rtl:space-x-reverse transition-colors ${
                isScrolled ? 'text-gray-700 hover:text-primary-600' : 'text-white hover:text-primary-300'
              }`}
            >
              <FaGlobe className="w-4 h-4" />
              <span className="text-sm font-medium">
                {language === 'ar' ? 'EN' : 'عربي'}
              </span>
            </button>

            {/* CTA Button */}
            <Link
              to="/join-us"
              className="btn-primary text-sm"
            >
              {language === 'ar' ? 'إنضم إلينا' : 'Join Us'}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 transition-colors ${
              isScrolled ? 'text-gray-700 hover:text-primary-600' : 'text-white hover:text-primary-300'
            }`}
          >
            {isOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-200"
            >
              <div className="px-4 py-6 space-y-4">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block text-xl font-bold transition-colors duration-200 ${
                      location.pathname === item.path
                        ? 'text-primary-600'
                        : 'text-gray-700 hover:text-primary-600'
                    }`}
                  >
                    {item.label[language]}
                  </Link>
                ))}
                
                <div className="pt-4 border-t border-gray-200 space-y-4">
                  <button
                    onClick={() => {
                      onLanguageChange(language === 'ar' ? 'en' : 'ar');
                      setIsOpen(false);
                    }}
                    className="flex items-center space-x-2 rtl:space-x-reverse text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    <FaGlobe className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {language === 'ar' ? 'English' : 'العربية'}
                    </span>
                  </button>
                  
                  <Link
                    to="/join-us"
                    onClick={() => setIsOpen(false)}
                    className="btn-primary block text-center"
                  >
                    {language === 'ar' ? 'إنضم إلينا' : 'Join Us'}
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar; 